import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

export type NavItem = { name: string; path: string; icon: React.ReactNode };
export type NavSection = { heading?: string; items: NavItem[] };

interface NavDropdownProps {
  label: string;
  icon: React.ReactNode;
  sections: NavSection[];
  accentActive: string;
  panelWidthClass?: string;
}

export const NavDropdown: React.FC<NavDropdownProps> = ({
  label,
  icon,
  sections,
  accentActive,
  panelWidthClass = 'w-56',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const childPaths = sections.flatMap((s) => s.items.map((i) => i.path));
  const isActive = childPaths.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors',
          isActive
            ? cn('bg-gray-100 dark:bg-gray-800', accentActive)
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800',
        )}
      >
        {icon}
        {label}
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50',
            panelWidthClass,
          )}
        >
          {sections.map((section, sIdx) => (
            <React.Fragment key={sIdx}>
              {section.heading && (
                <div className="px-4 pt-2 pb-1 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {section.heading}
                </div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={() => setOpen(false)}
                  className={({ isActive: linkActive }) =>
                    cn(
                      'flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                      linkActive
                        ? cn('bg-gray-100 dark:bg-gray-700', accentActive)
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
                    )
                  }
                >
                  {item.icon}
                  {item.name}
                </NavLink>
              ))}
              {sIdx < sections.length - 1 && (
                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
